// app.jsx — root assembly + slug routing + tab content

var { useEffect, useState, useMemo } = React;  // 5/25 v10.74: var — classic <script> 共 scope const 撞

// 5/24 v10.71 GAN-R12-B — defer interact.jsx (95 KB raw, 16 KB gz, interact-tab-only).
// live/lib/sub tab user 0 download interact.js → TTI 攻 -1.5-2s 期望.
// 模式: avatar.html 真不 sync load interact.js. <InteractRoom> 用 <InteractRoomLazy> 替, 真懒加载.
function InteractRoomLazy({ avatar }) {
  const [Comp, setComp] = useState(() => (typeof window !== "undefined" && window.InteractRoom) || null);
  useEffect(() => {
    if (Comp) return;
    if (typeof window === "undefined") return;
    if (window.InteractRoom) { setComp(() => window.InteractRoom); return; }
    if (window.__lazyInteractPromise) {
      window.__lazyInteractPromise.then(() => { if (window.InteractRoom) setComp(() => window.InteractRoom); });
      return;
    }
    window.__lazyInteractPromise = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = "_compiled/interact.js?v=10.71";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => { window.__lazyInteractPromise = null; resolve(); };
      document.body.appendChild(s);
    });
    window.__lazyInteractPromise.then(() => { if (window.InteractRoom) setComp(() => window.InteractRoom); });
  }, []);
  if (!Comp) {
    return React.createElement("div", { style: { padding: 40, textAlign: "center", color: "#a8a8b0" } }, "互动模块加载中…");
  }
  return React.createElement(Comp, { avatar });
}

function getSlug() {
  const u = new URL(window.location.href);
  return u.searchParams.get("slug");
}
function setSlug(slug) {
  const u = new URL(window.location.href);
  if (slug) u.searchParams.set("slug", slug);
  else u.searchParams.delete("slug");
  window.history.replaceState({}, "", u);
}

function App() {
  const [tweaks, setTweak] = useTweaks(window.__TWEAK_DEFAULTS__);
  const playback = usePlayback(window.CURRENT_SEGMENT.duration, 152);

  const slug = getSlug() || "xiaocheng";
  const avatar = useMemo(
    () => window.AVATARS_ROSTER.find(a => a.slug === slug) || window.AVATARS_ROSTER[0],
    [slug]
  );
  // 5/18 r2: query 参数支持 ?mode=interact 或 ?tab=<id>
  //   - mode=interact → interact tab (P3 LiveKit, 历史入口保留)
  //   - tab=library/subs/live/interact → 任意 tab (xiaocheng.html non-live tab 跳转入口)
  //   - 老 mode=live 保留兼容
  const _params = (typeof window !== "undefined")
    ? new URL(window.location.href).searchParams
    : null;
  const _initMode = _params?.get("mode") || null;
  const _initTab = _params?.get("tab") || null;
  const VALID_TABS = ["live", "library", "subs", "interact"];
  // 5/18 r4: shop-NNN slug 默认落 interact tab — 这些数字店长只有真互动 (WS persona + motion mp4),
  //          没有 24/7 HLS / 节目库. 用户从 index 点 进店 → 直接进真 WS 互动.
  const _isShopAvatar = /^shop-\d+$/.test(slug);
  // 5/18 v9.8: xiaocheng + mode=interact 用户挂断视频通话后, 老 10-min idle 计时把 activeTab 切到 "live" 显 mock 节目单 (founder 实测又踩雷). 这条路由强制 activeTab=interact, 任何切换被吞.
  // 5/24 v10.55 扩 lock: ANY mode=interact (含 shop-NNN + team) 都强制锁 interact tab.
  //   shop / team 没真 lore 节目单, 切去 live 会显 mock 商业财富史 — 完全错位 UX.
  const _xcInteractLock = _initMode === "interact";
  const [activeTab, setActiveTab] = useState(() => {
    if (_initMode === "interact") return "interact";
    if (_initTab && VALID_TABS.includes(_initTab)) return _initTab;
    if (_initMode === "live") return "live";
    if (_isShopAvatar) return "interact";
    return tweaks.initialTab || "live";
  });
  const _safeSetActiveTab = (next) => {
    if (_xcInteractLock && next !== "interact") {
      console.log("[xc-lock] swallow setActiveTab(", next, ") on xc+interact route");
      return;
    }
    setActiveTab(next);
  };

  useEffect(() => { document.title = `${avatar.name} · 万物 TV`; }, [avatar.name]);

  useEffect(() => {
    document.body.setAttribute("data-surface", tweaks.surface);
    document.body.setAttribute("data-density", tweaks.timelineDensity);
    document.documentElement.style.setProperty("--probe-orange", tweaks.accent);
    document.documentElement.style.setProperty("--tv-accent", tweaks.accent);
    document.documentElement.style.setProperty("--mk-accent", tweaks.accent);
  }, [tweaks.surface, tweaks.timelineDensity, tweaks.accent]);

  // re-render lucide icons whenever DOM changes
  useEffect(() => { if (window.lucide) window.lucide.createIcons(); });

  // 10-min idle → auto switch back to live (互动 tab only). v9.8 xc+interact 锁住不让切.
  useEffect(() => {
    if (activeTab !== "interact") return;
    if (_xcInteractLock) return;  // xc+interact 路由永远停 互动 tab, 不自动切 live mock
    const id = setTimeout(() => {
      setActiveTab("live");
    }, 10 * 60 * 1000);
    return () => clearTimeout(id);
  }, [activeTab, _xcInteractLock]);

  return (
    <React.Fragment>
      <div className={`tv-shell tab-${activeTab}`}>
        <Hero tweaks={tweaks} avatar={avatar} activeTab={activeTab} setActiveTab={_safeSetActiveTab} />

        {activeTab === "live" && (
          <main className="main-grid">
            <div className="col-left">
              <Player tweaks={tweaks} playback={playback} avatar={avatar} />
              <Transcript tweaks={tweaks} playback={playback} setTweak={setTweak} />
            </div>
            <div className="col-right">
              <div className="right-section-title"><span>接下来</span><span className="line"></span></div>
              <UpNext playback={playback} />
              <div className="right-section-title"><span>24h 主题轮转</span><span className="line"></span></div>
              <Timeline playback={playback} />
            </div>
          </main>
        )}

        {activeTab === "library" && <LibraryTab avatar={avatar} />}
        {activeTab === "subs" && <SubsTab avatar={avatar} />}

        {activeTab === "interact" && (
          <main className="main-grid main-grid-interact">
            <div className="col-left">
              <InteractRoomLazy avatar={avatar} />
            </div>
            <div className="col-right col-right-interact">
              <div className="interact-status-banner">
                <span className="icon"><i data-lucide="zap"></i></span>
                <div>
                  <div className="label">{avatar.name} 正在等待提问</div>
                  <div style={{ fontSize: 11, color: "var(--tv-fg-muted)" }}>向 TA 提问以开启对话</div>
                </div>
                <span className="arrow">↓</span>
              </div>

              {/* 5/18 r4: shop-NNN 数字店长无 24h 节目单 "接下来" mock, 改显店主真信息卡 */}
              {_isShopAvatar ? (
                <>
                  <div className="right-section-title"><span>店主名片</span><span className="line"></span></div>
                  <ShopInfoCard avatar={avatar} />
                </>
              ) : (
                <>
                  <div className="right-section-title"><span>接下来</span><span className="line"></span></div>
                  <UpNext playback={playback} />
                </>
              )}

              <div className="right-section-title"><span>商务转化</span><span className="line"></span></div>
              <SalesEntries avatar={avatar} />
            </div>
          </main>
        )}
      </div>

      {/* mobile bottom sticky CTA bar (interact tab only) */}
      {activeTab === "interact" && (
        <div className="mobile-cta-bar">
          <a href="#" className="cta-ghost">
            <i data-lucide="store"></i>
            找 TA 服务
          </a>
          <a href="#" className="cta-primary">
            <i data-lucide="sparkles"></i>
            同款数字人
          </a>
        </div>
      )}

      <TweaksUI tweaks={tweaks} setTweak={setTweak} />
    </React.Fragment>
  );
}

/* —— Library tab (placeholder, but designed) —— */
function LibraryTab({ avatar }) {
  const today = window.SCHEDULE_24H;
  return (
    <main className="library-tab">
      <header className="library-head">
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em" }}>{avatar.name} 的节目库</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--tv-fg-muted)" }}>
            按主题浏览 · {today.length} 个节目段 · 24h 持续更新
          </p>
        </div>
        <div className="chip" style={{ height: 32, fontSize: 12 }}>
          <i data-lucide="filter"></i>
          按主题筛选
        </div>
      </header>
      <div className="library-grid">
        {today.map((s) => (
          <div key={s.hour} className="library-card">
            <div className="library-card-time">{String(s.hour).padStart(2, "0")}:00</div>
            <div className="library-card-color" style={{ background: s.color }}/>
            <div className="library-card-body">
              <div className="library-card-topic">{s.topic}</div>
              <div className="library-card-name">{s.name}</div>
              <div className="library-card-chips">
                {s.chips.map((c, i) => <span key={i} className="slot-chip">{c}</span>)}
              </div>
            </div>
            <button className="library-card-play" aria-label="播放">
              <i data-lucide="play"></i>
            </button>
          </div>
        ))}
      </div>
    </main>
  );
}

/* —— Subs tab (placeholder, designed) —— */
/* —— 5/18 r5: ShopInfoCard — shop-NNN 店主真名片 (0 技术参数) —— */
function ShopInfoCard({ avatar }) {
  if (!avatar) return null;
  const country = window.COUNTRIES?.[avatar.country];
  const cat = window.CATEGORIES?.find(c => c.id === avatar.cat);
  const role = avatar.tags?.[0] || avatar.blurb || "店主";
  const rows = [
    { k: "店主", v: `${avatar.name}${country ? ` ${country.flag} ${country.zh}` : ""}` },
    { k: "角色", v: role },
    { k: "类目", v: cat?.zh || avatar.cat },
    { k: "年龄", v: avatar.age ? `${avatar.age} 岁` : null },
    { k: "语言", v: (avatar.langs || []).join(" · ") || "普通话" },
  ].filter(r => r.v);
  return (
    <div style={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 12,
      padding: "12px 14px",
      display: "grid", gap: 8,
    }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
          <span style={{ color: "var(--tv-fg-muted)" }}>{r.k}</span>
          <strong style={{ textAlign: "right", maxWidth: "60%" }}>{r.v}</strong>
        </div>
      ))}
    </div>
  );
}

// 5/18 §2.9 Phase 1: 真订阅 (匿名 client_id + localStorage 持久 + 节目单命中提醒).
// Phase 2 (deferred, 需登录态): mall-server tv_topic_subscription 表 + OpenIM 推送.
const SUBS_THEMES = [
  { id: "lore",    name: "学术快报",   sub: "古希腊 / 阿基米德 / 罗马铸币...",     color: "#8B5E83",
    match: ["学术", "古希腊", "罗马", "屈延文", "网络世界再现"] },
  { id: "humor",   name: "技术幽默",   sub: "凌晨调剂 · 程序员段子",               color: "#5A7B8C",
    match: ["技术幽默", "生活幽默", "段子"] },
  { id: "lore2",   name: "商业财富史", sub: "1869·黑色星期五 / 摩根 / 大萧条...",  color: "#FF752B",
    match: ["商业财富史", "财富", "黑色星期五", "摩根"] },
  { id: "ai",      name: "AI 史",      sub: "达特茅斯 / Hinton / LeNet / Transformer", color: "#A06EE1",
    match: ["第四代人机交互", "AI", "Hinton", "Transformer", "达特茅斯", "数字人", "数字团队"] },
  { id: "mine",    name: "矿业经济",   sub: "蒙古铜金 · 哈萨克铀油",               color: "#C68A3F",
    match: ["矿业", "蒙古", "哈萨克"] },
  { id: "finance", name: "整点金融脉搏", sub: "全球指数 · 收盘 · 加密简报",         color: "#3BD671",
    match: ["整点金融", "金融脉搏", "加密"] },
];
const SUBS_DEFAULT = { lore: true, humor: false, lore2: true, ai: true, mine: false, finance: true };

function _readSubsPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem("tv_subs_themes") || "null");
    return raw && typeof raw === "object" ? { ...SUBS_DEFAULT, ...raw } : { ...SUBS_DEFAULT };
  } catch { return { ...SUBS_DEFAULT }; }
}
function _ensureClientId() {
  try {
    let id = localStorage.getItem("tv_client_id");
    if (!id) {
      id = "tv_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
      localStorage.setItem("tv_client_id", id);
    }
    return id;
  } catch { return "tv_anon"; }
}

function SubsTab({ avatar }) {
  const [prefs, setPrefs] = useState(_readSubsPrefs);
  const [hits, setHits] = useState([]);
  const clientId = useMemo(_ensureClientId, []);

  const toggle = (id) => {
    setPrefs(p => {
      const next = { ...p, [id]: !p[id] };
      try { localStorage.setItem("tv_subs_themes", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const detectHits = () => {
      const sched = (typeof window !== "undefined" && window.SCHEDULE_24H) || [];
      if (!sched.length) { setHits([]); return; }
      const nowHour = new Date().getHours();
      const matched = [];
      const seen = new Set();
      for (const s of sched) {
        const delta = (s.hour - nowHour + 24) % 24;
        if (delta > 6) continue;  // 命中窗口 0-6 小时内
        const topic = s.topic || "";
        const name = s.name || "";
        const hay = topic + " " + name;
        for (const t of SUBS_THEMES) {
          if (!prefs[t.id]) continue;
          if (t.match.some(kw => hay.indexOf(kw) >= 0)) {
            const key = t.id + "_" + s.hour;
            if (seen.has(key)) continue;
            seen.add(key);
            const when = delta === 0
              ? "正在播放"
              : `${delta} 小时后 · ${String(s.hour).padStart(2, "0")}:00`;
            matched.push({ themeId: t.id, theme: t.name, topic, name, when, color: t.color, delta });
            break;
          }
        }
      }
      matched.sort((a, b) => a.delta - b.delta);
      setHits(matched.slice(0, 4));
    };
    detectHits();
    const onSched = () => detectHits();
    window.addEventListener("tv-schedule-updated", onSched);
    const id = setInterval(detectHits, 60_000);
    return () => {
      window.removeEventListener("tv-schedule-updated", onSched);
      clearInterval(id);
    };
  }, [prefs]);

  const activeCount = Object.values(prefs).filter(Boolean).length;

  return (
    <main className="subs-tab">
      <header className="library-head">
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em" }}>主题订阅</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--tv-fg-muted)" }}>
            订阅的主题段在节目单命中时会高亮提醒 · 已订阅 {activeCount} / {SUBS_THEMES.length}
          </p>
        </div>
      </header>
      {hits.length > 0 && (
        <section className="subs-hits" style={{
          margin: "12px 0 18px", padding: "12px 14px",
          background: "rgba(255,117,43,0.08)", border: "1px solid rgba(255,117,43,0.28)",
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 12, color: "var(--tv-fg-muted)", marginBottom: 6, letterSpacing: "0.04em" }}>
            📡 订阅命中
          </div>
          {hits.map(h => (
            <div key={h.themeId + "_" + h.delta} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", background: h.color, flex: "0 0 8px",
              }}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{h.theme} · {h.when}</div>
                <div style={{ fontSize: 12, color: "var(--tv-fg-muted)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {h.topic} · {h.name}
                </div>
              </div>
            </div>
          ))}
        </section>
      )}
      <div className="subs-grid">
        {SUBS_THEMES.map(t => {
          const on = !!prefs[t.id];
          return (
            <div key={t.id} className={`subs-card ${on ? "subs-card-on" : ""}`}>
              <div className="subs-card-swatch" style={{ background: t.color }}/>
              <div className="subs-card-body">
                <div className="subs-card-name">{t.name}</div>
                <div className="subs-card-sub">{t.sub}</div>
              </div>
              <label className="subs-toggle" onClick={(e) => { e.preventDefault(); toggle(t.id); }}>
                <input type="checkbox" checked={on} readOnly/>
                <span className="subs-toggle-track"><span className="subs-toggle-thumb"/></span>
              </label>
            </div>
          );
        })}
      </div>
      <p style={{ margin: "16px 4px 0", fontSize: 11, color: "var(--tv-fg-muted)", lineHeight: 1.5 }}>
        客户端 ID: <code style={{ opacity: 0.7 }}>{clientId.slice(0, 16)}…</code> · 偏好已保存到本机 · 登录后将同步到您账户 (Phase 2)
      </p>
    </main>
  );
}

/* —— Tweaks panel —— */
function TweaksUI({ tweaks, setTweak }) {
  return (
    <TweaksPanel title="Tweaks · 万物 TV">
      <TweakSection label="视觉" />
      <TweakColor
        label="主题色"
        value={tweaks.accent}
        options={["#F69155", "#FF752B", "#DD3C28", "#3BD671"]}
        onChange={(v) => setTweak("accent", v)}
      />
      <TweakRadio
        label="表面"
        value={tweaks.surface}
        options={["warmDark", "midnight", "paper"]}
        onChange={(v) => setTweak("surface", v)}
      />
      <TweakToggle
        label="LIVE 徽章"
        value={tweaks.showLiveBadge}
        onChange={(v) => setTweak("showLiveBadge", v)}
      />

      <TweakSection label="字幕" />
      <TweakSlider
        label="字幕字号"
        value={tweaks.captionSize}
        min={16} max={32} step={1} unit=" px"
        onChange={(v) => setTweak("captionSize", v)}
      />
      <TweakRadio
        label="转写模式"
        value={tweaks.transcriptMode}
        options={["current", "full"]}
        onChange={(v) => setTweak("transcriptMode", v)}
      />

      <TweakSection label="时间线" />
      <TweakRadio
        label="密度"
        value={tweaks.timelineDensity}
        options={["comfy", "dense"]}
        onChange={(v) => setTweak("timelineDensity", v)}
      />
    </TweaksPanel>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
